import { Injectable } from '@angular/core';
import { NavigationCancel, NavigationEnd, NavigationError, NavigationSkipped, Router, UrlTree } from '@angular/router';
import { filter, firstValueFrom, take, timeout } from 'rxjs';
import { TranslocoService } from '@jsverse/transloco';
import { NotificationService } from 'shared-service/services/notification/notification.service';
import { eNotificationType } from 'shared-domains/utilities/notification.helper';
import { WorkspaceNavigationService } from '../workspace/workspace-navigation.service';
import { NotificationNavigationSource, notificationNavigationTarget } from './notification-navigation-target';

/** Shared by the bell and inbox. Navigation never marks read or performs a business operation. */
@Injectable({ providedIn: 'root' })
export class NotificationNavigationService {
    readonly target = notificationNavigationTarget;
    private busy = false;

    constructor(private router: Router, private workspace: WorkspaceNavigationService,
        private transloco: TranslocoService, private notification: NotificationService) {}

    link(item: NotificationNavigationSource): UrlTree | null {
        const target = this.target(item);
        return target ? this.router.createUrlTree(target.commands, {
            queryParams: this.workspace.queryParams(target.queryParams, item.TenantId),
        }) : null;
    }

    open(item: NotificationNavigationSource): Promise<boolean> {
        const link = this.link(item);
        if (!link) {
            this.showError('notificationInbox.navigation-unavailable');
            return Promise.resolve(false);
        }
        return this.navigate(link);
    }

    viewAll(): Promise<boolean> {
        return this.navigate(this.router.createUrlTree(['/notifications'], { queryParams: this.workspace.queryParams() }));
    }

    private async navigate(link: UrlTree): Promise<boolean> {
        if (this.busy) return false;
        this.busy = true;
        try {
            await this.router.navigateByUrl(link);
            // A destination may normalize its tab query while the first navigation completes.
            // Wait for that follow-up before checking the actual destination.
            if (this.router.getCurrentNavigation?.()) {
                await firstValueFrom(this.router.events.pipe(
                    filter(event => event instanceof NavigationEnd || event instanceof NavigationCancel
                        || event instanceof NavigationError || event instanceof NavigationSkipped),
                    take(1), timeout(5000),
                ));
            }
            // Guards can complete navigation on an access-denied page. Do not report success then.
            const arrived = this.router.parseUrl(this.router.url);
            const samePath = arrived.root.toString() === link.root.toString();
            const sameWorkspace = arrived.queryParams['workspaceTenantId'] === String(link.queryParams['workspaceTenantId']);
            const sameTarget = Object.entries(link.queryParams).every(([key, value]) =>
                String(arrived.queryParams[key]) === String(value));
            if (!samePath || !sameWorkspace || !sameTarget) {
                this.showError('notificationInbox.navigation-failed');
                return false;
            }
            return true;
        } catch {
            this.showError('notificationInbox.navigation-failed');
            return false;
        } finally {
            this.busy = false;
        }
    }

    private showError(key: string): void {
        this.notification.showNotification(this.transloco.translate(key), eNotificationType.ERROR);
    }
}
